import React from "react";
import { StickyCta } from "./StepIntro";
import { RadioCard } from "./RadioCard";
import { DisclaimerBanner } from "./DisclaimerBanner";
import { Textarea } from "../ui/textarea";
import {
    PHASE2_HEADER,
    PHASE2_INTRO,
    PHASE2_SUBMIT_LABEL,
} from "../../lib/content";

/**
 * One Phase-2 AI question per screen. Supports both single_choice and text.
 */
export function StepPhase2({ index, questions, answers, onAnswer, onNext, catName }) {
    const q = questions[index];
    const value = answers[q.id] || "";
    const isLast = index === questions.length - 1;
    const canContinue =
        q.type === "text" ? value.trim().length >= 1 : Boolean(value);

    return (
        <div className="pb-24" data-testid={`step-phase2-${q.id}`}>
            <div className="mt-2 space-y-2">
                <span className="eyebrow">{PHASE2_HEADER}</span>
                <p className="text-[13px] leading-relaxed text-meow-muted">
                    {PHASE2_INTRO}
                </p>
            </div>

            <div className="mt-5 flex items-center justify-between">
                <span
                    className="inline-flex items-center rounded-full bg-meow-icon px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-widest text-meow-teal"
                    data-testid="phase2-category-pill"
                >
                    {String(q.category || "").replaceAll("_", " ")}
                </span>
                <span className="text-xs font-medium text-meow-muted tabular-nums">
                    Question {index + 1} of {questions.length}
                </span>
            </div>

            <h1
                className="mt-4 font-display text-[1.5rem] font-medium leading-snug text-meow-charcoal"
                data-testid="phase2-question-label"
            >
                {q.label}
            </h1>
            {catName ? (
                <p className="mt-1.5 text-[13px] text-meow-muted">
                    About{" "}
                    <span className="font-medium text-meow-charcoal">{catName}</span>
                </p>
            ) : null}

            {q.type === "text" ? (
                <div className="mt-5">
                    <Textarea
                        value={value}
                        onChange={(e) => onAnswer(q.id, e.target.value)}
                        placeholder="Type your answer…"
                        rows={5}
                        className="rounded-2xl border-transparent bg-white p-4 text-base focus-visible:border-meow-teal focus-visible:ring-2 focus-visible:ring-meow-teal/20"
                        data-testid={`phase2-${q.id}-text-input`}
                    />
                </div>
            ) : (
                <div className="mt-5 space-y-2.5" role="radiogroup" data-testid={`phase2-options-${q.id}`}>
                    {(q.options || []).map((opt, i) => (
                        <RadioCard
                            key={opt}
                            value={opt}
                            label={opt}
                            checked={value === opt}
                            onSelect={(v) => onAnswer(q.id, v)}
                            testId={`phase2-${q.id}-opt-${i}`}
                        />
                    ))}
                </div>
            )}

            <div className="mt-6">
                <DisclaimerBanner />
            </div>

            <StickyCta>
                <button
                    type="button"
                    className="meow-btn-primary w-full"
                    disabled={!canContinue}
                    onClick={onNext}
                    data-testid="phase2-next-btn"
                >
                    {isLast ? PHASE2_SUBMIT_LABEL : "Next question"}
                </button>
            </StickyCta>
        </div>
    );
}
